import {
  AIAgentInsights,
  CRMChannel,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { useMemo } from "react";
import { useAgentAnalyticsContext } from "../providers/useAgentAnalyticsContext";

const CHANNELS = [
  CRMChannel.whatsapp,
  CRMChannel.instagram,
  CRMChannel.chatbot,
  CRMChannel.twilio,
  CRMChannel.email,
  CRMChannel.voice,
] as const;

// Insights responses aren't schema-validated, so a channel can come back missing
// entirely or with a partial payload (e.g. a channel the org never provisioned).
// Missing values count as zeros instead of poisoning every total with NaN.
const normalizeInsights = (
  insights: AIAgentInsights | undefined
): AIAgentInsights => ({
  ai_replies: insights?.ai_replies ?? 0,
  human_sent_messages: insights?.human_sent_messages ?? 0,
  missed_ai_reply: insights?.missed_ai_reply ?? 0,
  resolved: insights?.resolved ?? 0,
  total_ai_conversations: insights?.total_ai_conversations ?? 0,
  total_conversations: insights?.total_conversations ?? 0,
  unresolved: insights?.unresolved ?? 0,
});

export interface ChatsOverviewKPITotals {
  totalConversations: number;
  totalMessages: number;
  aiReplies: number;
  humanSentMessages: number;
  totalAiConversations: number;
  resolved: number;
  unresolved: number;
  missedAiReply: number;
}

export const useChatsOverviewKPIData = (entityId: string) => {
  const { integrationsApi } = useRestApiProvider();
  const { timeRange } = useAgentAnalyticsContext();

  const {
    data: whatsappInsights,
    isLoading: isWhatsappLoading,
    isFetching: isWhatsappFetching,
    isError: isWhatsappError,
    refetch: whatsappRefetch,
  } = integrationsApi.useGetAIAgentInsightsQuery({
    entityId,
    channel: CRMChannel.whatsapp,
    start_time: timeRange.fromISO,
    end_time: timeRange.toISO,
  });

  const {
    data: instagramInsights,
    isLoading: isInstagramLoading,
    isFetching: isInstagramFetching,
    isError: isInstagramError,
    refetch: instagramRefetch,
  } = integrationsApi.useGetAIAgentInsightsQuery({
    entityId,
    channel: CRMChannel.instagram,
    start_time: timeRange.fromISO,
    end_time: timeRange.toISO,
  });

  const {
    data: chatbotInsights,
    isLoading: isChatbotLoading,
    isFetching: isChatbotFetching,
    isError: isChatbotError,
    refetch: chatbotRefetch,
  } = integrationsApi.useGetAIAgentInsightsQuery({
    entityId,
    channel: CRMChannel.chatbot,
    start_time: timeRange.fromISO,
    end_time: timeRange.toISO,
  });

  const {
    data: twilioInsights,
    isLoading: isTwilioLoading,
    isFetching: isTwilioFetching,
    isError: isTwilioError,
    refetch: twilioRefetch,
  } = integrationsApi.useGetAIAgentInsightsQuery({
    entityId,
    channel: CRMChannel.twilio,
    start_time: timeRange.fromISO,
    end_time: timeRange.toISO,
  });

  const {
    data: emailInsights,
    isLoading: isEmailLoading,
    isFetching: isEmailFetching,
    isError: isEmailError,
    refetch: emailRefetch,
  } = integrationsApi.useGetAIAgentInsightsQuery({
    entityId,
    channel: CRMChannel.email,
    start_time: timeRange.fromISO,
    end_time: timeRange.toISO,
  });

  const {
    data: voiceInsights,
    isLoading: isVoiceLoading,
    isFetching: isVoiceFetching,
    isError: isVoiceError,
    refetch: voiceRefetch,
  } = integrationsApi.useGetAIAgentInsightsQuery({
    entityId,
    channel: CRMChannel.voice,
    start_time: timeRange.fromISO,
    end_time: timeRange.toISO,
  });

  const insightsByChannel: Partial<
    Record<CRMChannel, AIAgentInsights | undefined>
  > = useMemo(
    () => ({
      [CRMChannel.whatsapp]: whatsappInsights,
      [CRMChannel.instagram]: instagramInsights,
      [CRMChannel.chatbot]: chatbotInsights,
      [CRMChannel.twilio]: twilioInsights,
      [CRMChannel.email]: emailInsights,
      [CRMChannel.voice]: voiceInsights,
    }),
    [
      whatsappInsights,
      instagramInsights,
      chatbotInsights,
      twilioInsights,
      emailInsights,
      voiceInsights,
    ]
  );

  const totals = useMemo<ChatsOverviewKPITotals | null>(() => {
    const items = CHANNELS.map((ch) => insightsByChannel[ch]);

    // Nothing has loaded yet - `isLoading` covers this state in the UI
    if (items.every((item) => item == null)) {
      return null;
    }

    return items.map(normalizeInsights).reduce<ChatsOverviewKPITotals>(
      (acc, item) => ({
        totalConversations: acc.totalConversations + item.total_conversations,
        totalMessages:
          acc.totalMessages + item.ai_replies + item.human_sent_messages,
        aiReplies: acc.aiReplies + item.ai_replies,
        humanSentMessages: acc.humanSentMessages + item.human_sent_messages,
        totalAiConversations:
          acc.totalAiConversations + item.total_ai_conversations,
        resolved: acc.resolved + item.resolved,
        unresolved: acc.unresolved + item.unresolved,
        missedAiReply: acc.missedAiReply + item.missed_ai_reply,
      }),
      {
        totalConversations: 0,
        totalMessages: 0,
        aiReplies: 0,
        humanSentMessages: 0,
        totalAiConversations: 0,
        resolved: 0,
        unresolved: 0,
        missedAiReply: 0,
      }
    );
  }, [insightsByChannel]);

  const isLoading =
    isWhatsappLoading ||
    isWhatsappFetching ||
    isInstagramLoading ||
    isInstagramFetching ||
    isChatbotLoading ||
    isChatbotFetching ||
    isTwilioLoading ||
    isTwilioFetching ||
    isEmailLoading ||
    isEmailFetching ||
    isVoiceLoading ||
    isVoiceFetching;

  const isError =
    isWhatsappError ||
    isInstagramError ||
    isChatbotError ||
    isTwilioError ||
    isEmailError ||
    isVoiceError;

  const refetch = () => {
    if (isWhatsappError) whatsappRefetch();
    if (isInstagramError) instagramRefetch();
    if (isChatbotError) chatbotRefetch();
    if (isTwilioError) twilioRefetch();
    if (isEmailError) emailRefetch();
    if (isVoiceError) voiceRefetch();
  };

  return {
    totals,
    insightsByChannel,
    isLoading,
    isError,
    refetch,
  };
};
